import {
  AddUser1Icon,
  Calendar1Icon,
  CardViewIcon,
  DeleteIcon,
  Divider,
  GKButton,
  GKTextField,
  Grid2,
  IconButton,
  KanbanViewIcon,
  NotesIcon,
  PlusIcon,
  Popover,
  SearchIcon,
  Show,
} from "components";
import GKKanban from "components/GKKanban";
import GKNoDataFound from "components/GKNoDataFound";
import { useFetchQuery, useMutation } from "hooks/useQueries";
import { groupBy } from "lodash";
import { useState } from "react";
import { updateFilter } from "store/slices/tableSlice";
import { useAppDispatch, useAppSelector } from "store/store";
import { paletteLightTheme } from "utils/ColorTheme";
import { GET_LEAD_STATUS } from "utils/apiRoutes";
import axiosInstance from "utils/axiosInstance";
import { FetchDataURL } from "utils/common";
import { refetchQuery } from "utils/helpers";
import { QueryKeys } from "utils/queryKey";
import AddNewMeeting from "./AddMeeting";
import AddNewNotes from "./AddNotes";
import AddStatusAndSouceModal from "./AddStatusAndSouceModal";
import CreateLeadModal from "./CreateLeadModal";

interface BoardType {
  columns: {
    id: number;
    title: string;
    cards: any[];
  }[];
}

interface Props {
  isLeadStatus: boolean;
  setIsLeadStatus: (isLeadStatus: boolean) => void;
}

const LeadStatusPage = ({ isLeadStatus, setIsLeadStatus }: Props) => {
  const dispatch = useAppDispatch();

  const commonFilter = useAppSelector(
    (state) => state?.table?.commonFilter["lead"],
  );

  const [cardActionAnchor, setCardActionAnchor] =
    useState<HTMLButtonElement | null>(null);

  const [leadModalOpen, setLeadModalOpen] = useState(false);

  const [addStatusModal, setAddStuatusModal] = useState(false);

  const [controlledBoard, setBoard] = useState<BoardType>({
    columns: [],
  });

  const [isScheduleMeeting, setIsScheduleMeeting] = useState(false);

  const [meetingModal, setMeetingModal] = useState(false);

  const [leadId, setLeadId] = useState(null);

  const [openNoteModal, setOpenNoteModal] = useState(false);

  // ===###===###===###===###===###===###===###===###===###===###===###===###===###===###
  const {
    isFetched: statusFetched,
    refetch: getStatusRefetch,
    isFetching: leadStatusLoading,
  } = useFetchQuery({
    key: [QueryKeys.GET_STATUS],
    route: GET_LEAD_STATUS,
    success: (data: any) => {
      const newColumns = data?.data?.data?.map((dataItem: any) => {
        return {
          id: dataItem?.id,
          title: dataItem?.leadstatus,
          // @ts-ignore
          cards: [],
        };
      });

      setBoard({
        columns: newColumns,
      });
      refetchQuery("GET_ALL_LEADS");
    },
  });
  // ===###===###===###===###===###===###===###===###===###===###===###===###===###===###
  const { refetch: getLeadsRefetch, isFetching: leadsLoading } = useFetchQuery({
    key: ["GET_ALL_LEADS", commonFilter],
    route: FetchDataURL.LEAD_LIST,
    requestBody: {
      search: commonFilter?.search,
    },
    enabled: statusFetched,
    success: (data: any) => {
      const groupedData = groupBy(data?.data?.data, "leadStatusStr");
      const newColumns = controlledBoard?.columns?.map((dataItem) => {
        return {
          ...dataItem,
          cards:
            groupedData[dataItem?.title]?.map((item: any) => {
              return {
                ...item,
                title: item?.name,
              };
            }) || [],
        };
      });
      setBoard({
        columns: newColumns,
      });
    },
  });
  // ===###===###===###===###===###===###===###===###===###===###===###===###===###===###
  const { mutate: updateLead } = useMutation({
    mutationFn: (data: any) =>
      axiosInstance.patch(`/lead/${data?.id}/change-status/`, {
        leadStatus: data?.leadStatus,
      }),
    onSuccess: () => {
      refetchQuery(QueryKeys.GET_LEAD);
      refetchQuery("GET_ALL_LEADS");
    },
    onError: () => {
      getLeadsRefetch();
      getStatusRefetch();
    },
  });
  // ===###===###===###===###===###===###===###===###===###===###===###===###===###===###
  const { mutate: handledConvertLead } = useMutation({
    mutationFn: (da: any) => axiosInstance.post(`/client/${da}/convert/`),
    mutationKey: ["DELETE_LEAD"],
    onSuccess: () => {
      refetchQuery(QueryKeys.GET_LEAD);
      setCardActionAnchor(null);
      refetchQuery("GET_ALL_LEADS");
    },
  });
  // ===###===###===###===###===###===###===###===###===###===###===###===###===###===###
  const { mutate: handledDeleteLead } = useMutation({
    mutationFn: (da: any) => axiosInstance.delete(`/lead/${da}`),
    mutationKey: ["DELETE_LEAD"],
    onSuccess: () => {
      getLeadsRefetch();
    },
  });
  // ===###===###===###===###===###===###===###===###===###===###===###===###===###===###
  const loading = leadsLoading || leadStatusLoading;

  return (
    <>
      <Grid2
        container
        spacing={1}
        p={1}
        sx={{
          "& .react-kanban-column": {
            backgroundColor: `${"var(--white)"}`,
            padding: 0,
          },
        }}
      >
        <Grid2 size={2}>
          <GKTextField
            placeholder={"Search..."}
            sx={{
              "& .MuiInputBase-root ": {
                height: "35px",
              },
              "& .MuiOutlinedInput-notchedOutline": {
                border: "1px solid #E3E8EF",
              },
            }}
            value={commonFilter?.search}
            onChange={(event) => {
              dispatch(
                updateFilter({
                  name: "lead",
                  filter: { search: event.target.value },
                }),
              );
            }}
            type="search"
            slotProps={{
              input: {
                startAdornment: (
                  <SearchIcon width={26} color={"var(--neutral-500)"} />
                ),
              },
            }}
          />
        </Grid2>
        <Grid2 size={10}>
          <Grid2 container justifyContent={"flex-end"} gap={1}>
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                borderRadius: 8,
                gap: 4,
                padding: 4,
                border: `1px solid ${paletteLightTheme.palette.border.main}`,
              }}
            >
              <IconButton
                sx={{
                  background: !isLeadStatus
                    ? "var(--white)"
                    : "var(--primary-900)",
                  color: !isLeadStatus ? "var(--neutral-500)" : "var(--white)",
                  borderRadius: 2,
                  ":hover": {
                    background: !isLeadStatus
                      ? "var(--white)"
                      : "var(--primary-900)",
                    color: !isLeadStatus
                      ? "var(--neutral-500)"
                      : "var(--white)",
                  },
                }}
                onClick={() => {
                  setIsLeadStatus(!isLeadStatus);
                }}
              >
                <KanbanViewIcon />
              </IconButton>
              <IconButton
                sx={{
                  background: isLeadStatus
                    ? "var(--white)"
                    : "var(--primary-900)",
                  color: isLeadStatus ? "var(--neutral-500)" : "var(--white)",
                  borderRadius: 2,
                  ":hover": {
                    background: isLeadStatus
                      ? "var(--white)"
                      : "var(--primary-900)",
                    color: isLeadStatus ? "var(--neutral-500)" : "var(--white)",
                  },
                }}
                onClick={() => {
                  setIsLeadStatus(!isLeadStatus);
                }}
              >
                <CardViewIcon />
              </IconButton>
            </div>
            <GKButton
              variant="contained"
              size="medium"
              startIcon={<PlusIcon width={14} />}
              onClick={() => setLeadModalOpen(true)}
            >
              Add New Lead
            </GKButton>
            <GKButton
              color="dark"
              size="medium"
              variant="outlined"
              startIcon={<PlusIcon width={14} />}
              onClick={() => setAddStuatusModal(true)}
            >
              Add New Status
            </GKButton>
          </Grid2>
        </Grid2>
        <Grid2 size={12}>
          <Divider />
        </Grid2>
        {!loading &&
        controlledBoard.columns?.map((i) => i.cards)?.flat()?.length === 0 ? (
          <Grid2
            size={12}
            display={"flex"}
            justifyContent={"center"}
            alignItems={"center"}
          >
            <GKNoDataFound />
          </Grid2>
        ) : (
          <GKKanban
            updateLead={updateLead}
            columns={controlledBoard}
            setColumns={setBoard}
            setLeadId={setLeadId}
            setCardActionAnchor={setCardActionAnchor}
          />
        )}
      </Grid2>

      {leadModalOpen && (
        <CreateLeadModal open={leadModalOpen} setOpen={setLeadModalOpen} />
      )}

      {addStatusModal && (
        <AddStatusAndSouceModal
          open={addStatusModal}
          setOpen={setAddStuatusModal}
          type={"STATUS"}
        />
      )}

      <Popover
        open={Boolean(cardActionAnchor)}
        anchorEl={cardActionAnchor}
        onClose={() => {
          setCardActionAnchor(null);
        }}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        PaperProps={{
          style: { width: "14%", borderRadius: 8 },
        }}
      >
        <Grid2 container sx={{ py: "6px" }}>
          <Grid2
            size={12}
            borderBottom={`solid 1px ${paletteLightTheme.palette.border.main}`}
          >
            <GKButton
              onClick={() => {
                handledConvertLead(leadId);
              }}
              startIcon={
                <AddUser1Icon
                  height={16}
                  width={16}
                  color={"var(--neutral-500)"}
                />
              }
              color="dark"
              fullWidth
              sx={{
                px: 2,
                py: 1,
                borderRadius: 0,
                justifyContent: "flex-start",
              }}
            >
              Convert to Client
            </GKButton>
          </Grid2>
          <Grid2
            size={12}
            borderBottom={`solid 1px ${paletteLightTheme.palette.border.main}`}
          >
            <GKButton
              startIcon={
                <Calendar1Icon
                  width={16}
                  height={16}
                  color={"var(--neutral-500)"}
                />
              }
              color="dark"
              fullWidth
              sx={{
                px: 2,
                py: 1,
                borderRadius: 0,
                justifyContent: "flex-start",
              }}
              onClick={() => {
                setMeetingModal(true);
                setIsScheduleMeeting(false);
                setCardActionAnchor(null);
              }}
            >
              Add Meeting
            </GKButton>
          </Grid2>
          <Grid2
            size={12}
            borderBottom={`solid 1px ${paletteLightTheme.palette.border.main}`}
          >
            <GKButton
              startIcon={
                <NotesIcon
                  width={16}
                  height={16}
                  color={"var(--neutral-500)"}
                />
              }
              color="dark"
              fullWidth
              sx={{
                px: 2,
                py: 1,
                borderRadius: 0,
                justifyContent: "flex-start",
              }}
              onClick={() => {
                setOpenNoteModal(true);
                setCardActionAnchor(null);
              }}
            >
              Add Notes
            </GKButton>
          </Grid2>
          <Grid2 size={12}>
            <GKButton
              onClick={() => {
                handledDeleteLead(leadId);
                setCardActionAnchor(null);
              }}
              startIcon={<DeleteIcon width={16} color={"var(--neutral-500)"} />}
              color="dark"
              fullWidth
              sx={{
                px: 2,
                py: 1,
                borderRadius: 0,
                justifyContent: "flex-start",
              }}
            >
              Delete
            </GKButton>
          </Grid2>
        </Grid2>
      </Popover>

      <Show.When isTrue={meetingModal}>
        <AddNewMeeting
          open={meetingModal}
          setOpen={setMeetingModal}
          leadId={leadId}
          meetingData={null}
          refetch={getLeadsRefetch}
          schedule={isScheduleMeeting}
        />
      </Show.When>

      <Show.When isTrue={openNoteModal}>
        <AddNewNotes
          open={openNoteModal}
          setOpen={setOpenNoteModal}
          leadId={leadId}
          refetch={getLeadsRefetch}
          noteData={null}
        />
      </Show.When>
    </>
  );
};

export default LeadStatusPage;
